// Animation type constants
const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// New animation classes
const ANIMATE_FADE_IN = 'animate--fade-in';
const ANIMATE_SLIDE_UP = 'animate--slide-up';
const ANIMATE_SLIDE_DOWN = 'animate--slide-down';
const ANIMATE_SLIDE_LEFT = 'animate--slide-left';
const ANIMATE_SLIDE_RIGHT = 'animate--slide-right';

// Animation configuration
const ANIMATION_CONFIG = {
  [ANIMATE_FADE_IN]: {
    initial: { opacity: 0 },
    animate: { opacity: 1 }
  },
  [ANIMATE_SLIDE_UP]: {
    initial: { transform: 'translateY(20px)', opacity: 0 },
    animate: { transform: 'translateY(0)', opacity: 1 }
  },
  [ANIMATE_SLIDE_DOWN]: {
    initial: { transform: 'translateY(-20px)', opacity: 0 },
    animate: { transform: 'translateY(0)', opacity: 1 }
  },
  [ANIMATE_SLIDE_LEFT]: {
    initial: { transform: 'translateX(30px)', opacity: 0 },
    animate: { transform: 'translateX(0)', opacity: 1 }
  },
  [ANIMATE_SLIDE_RIGHT]: {
    initial: { transform: 'translateX(-30px)', opacity: 0 },
    animate: { transform: 'translateX(0)', opacity: 1 }
  }
};

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        requestAnimationFrame(() => {
          // Remove offscreen class and handle cascade
          elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
          
          // Apply cascade delay if specified
          if (elementTarget.hasAttribute('data-cascade')) {
            const delay = elementTarget.getAttribute('data-cascade-delay') || '100';
            elementTarget.style.setProperty('--animation-order', index);
            elementTarget.style.setProperty('--animation-delay', `${delay}ms`);
          }
          
          // Apply animation based on class
          Object.keys(ANIMATION_CONFIG).forEach(animationClass => {
            if (elementTarget.classList.contains(animationClass)) {
              const config = ANIMATION_CONFIG[animationClass];
              Object.entries(config.initial).forEach(([prop, value]) => {
                elementTarget.style[prop] = value;
              });
              
              // Trigger reflow
              void elementTarget.offsetHeight;
              
              // Apply animation
              elementTarget.style.transition = 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)';
              Object.entries(config.animate).forEach(([prop, value]) => {
                elementTarget.style[prop] = value;
              });
            }
          });
        });
      }
      observer.unobserve(elementTarget);
    } else if (element.isIntersecting === false) {
      // Only reset if we're not in design mode
      if (!element.target.classList.contains('scroll-trigger--design-mode')) {
        requestAnimationFrame(() => {
          element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
          element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
          // Reset animation styles
          Object.values(ANIMATION_CONFIG).forEach(config => {
            Object.keys(config.initial).forEach(prop => {
              element.target.style[prop] = '';
            });
          });
          element.target.style.transition = '';
        });
      }
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  let ticking = false;
  let lastScrollY = window.scrollY;

  // Cache elements and their visibility
  const elements = animationTriggerElements.map(el => ({
    el,
    visible: false,
    observer: new IntersectionObserver(([entry]) => {
      elements.find(item => item.el === entry.target).visible = entry.isIntersecting;
    }, { threshold: 0.1 })
  }));

  // Observe all elements
  elements.forEach(item => item.observer.observe(item.el));

  // Throttled scroll handler
  const handleScroll = () => {
    lastScrollY = window.scrollY;
    if (!ticking) {
      window.requestAnimationFrame(() => {
        elements.forEach(item => {
          if (item.visible) {
            const ratio = 1 + scaleAmount * percentageSeen(item.el);
            item.el.style.setProperty('--zoom-in-ratio', ratio);
          }
        });
        ticking = false;
      });
      ticking = true;
    }
  };

  // Passive scroll listener for better performance
  window.addEventListener('scroll', handleScroll, { passive: true });

  // Cleanup
  return () => {
    window.removeEventListener('scroll', handleScroll);
    elements.forEach(item => item.observer.disconnect());
  };
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
